// Wraps a Refine DataProvider to apply per-resource transforms on read and write operations.
// Read transforms: enrich records (e.g., compute pricing on Transaction).
// Write transforms: strip relation fields and generated columns before mutations.

import type {
  BaseKey,
  BaseRecord,
  CrudFilter,
  CrudFilters,
  CrudSort,
  DataProvider,
  GetListParams,
  GetListResponse,
  GetManyParams,
  GetManyResponse,
  GetOneParams,
  GetOneResponse,
} from "@refinedev/core";

/**
 * A transform function that enriches a single record on read. The registry is a
 * type-erasure boundary: concrete per-resource transforms (e.g. `enrichTransaction`)
 * are cast to this shape when registered, and the result is cast back to the
 * caller's record type at the call sites below.
 */
export type RecordTransform = (record: unknown) => unknown;

/** A transform that strips non-DB fields before write. */
export type WriteTransform = (values: Record<string, unknown>) => Record<string, unknown>;

/** Registry mapping resource names to transform functions. */
export type TransformRegistry = {
  read: Record<string, RecordTransform>;
  write: Record<string, WriteTransform>;
  /**
   * Resources whose getList should run as two requests — an id-only page,
   * then a full-row fetch for those ids — instead of one `select *` query.
   * Only worth it for resources expensive enough that avoiding full
   * materialization of the unpaged columns outweighs the extra round trip.
   */
  twoPhaseList?: readonly string[];
  /**
   * Per-resource computed (view-derived) column names. A getList whose
   * filters target any of these falls back to a single fetch — see
   * canUseTwoPhaseList for the rationale.
   */
  twoPhaseComputedFields?: Readonly<Record<string, readonly string[]>>;
};

/** Ensure "in" operator values are always arrays (fixes URL deserialization of single values). */
function sanitizeInFilters(filters: CrudFilters): CrudFilters {
  return filters.map((filter): CrudFilter => {
    if ("field" in filter && filter.operator === "in" && !Array.isArray(filter.value)) {
      return { ...filter, value: [filter.value] };
    }
    if ("operator" in filter && (filter.operator === "or" || filter.operator === "and")) {
      return { ...filter, value: sanitizeInFilters(filter.value) };
    }
    return filter;
  });
}

/**
 * Trim leading/trailing whitespace from TOP-LEVEL string values (issue #205).
 * Nested objects/arrays are jsonb payloads (e.g. `check_overrides`) or typed
 * values that must not be walked. Every UI mutation funnels through this
 * provider, so this is the one place trimming covers all form paths.
 */
function trimStringValues(values: Record<string, unknown>): Record<string, unknown> {
  let changed = false;
  const out: Record<string, unknown> = { ...values };
  for (const [key, value] of Object.entries(values)) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed !== value) {
        out[key] = trimmed;
        changed = true;
      }
    }
  }
  return changed ? out : values;
}

/** Trim, then apply the per-resource write transform (if any). */
function writeVariables(
  variables: unknown,
  transform: ((values: Record<string, unknown>) => Record<string, unknown>) | undefined,
): unknown {
  const record =
    typeof variables === "object" && variables !== null && !Array.isArray(variables)
      ? trimStringValues(variables as Record<string, unknown>)
      : variables;
  if (!transform || typeof record !== "object" || record === null) return record;
  return transform(record as Record<string, unknown>);
}

/** True if any sorter targets a joined-table column (e.g. "product.name") —
 * the base provider expands its select to pull in that table, which an
 * id-only phase-1 query for two-phase getList can't express. */
function sortHasDottedField(sorters?: CrudSort[]): boolean {
  return (sorters ?? []).some((sorter) => sorter.field.includes("."));
}

/** True if any filter leaf field satisfies `pred`. Recurses through
 * "and"/"or" groups. */
function someFilterField(filters: CrudFilters | undefined, pred: (field: string) => boolean): boolean {
  return (filters ?? []).some((filter) =>
    "field" in filter ? pred(filter.field) : someFilterField(filter.value, pred),
  );
}

/**
 * Two-phase getList only pays off for a plain paged list against a
 * registered resource: dotted sorter/filter fields need a joined-table
 * select that phase 1's id-only query can't express, and phase 2 (getMany)
 * applies no sort/filter/count of its own. Very small pages don't amortize
 * the extra round trip; very large ones risk an oversized `.in()` clause in
 * phase 2 — both fall back to single-phase.
 *
 * Filters on the resource's registered computed columns also fall back to a
 * single fetch: evaluating such a WHERE already forces the view's expensive
 * laterals for every candidate row, so the id-only phase saves little and
 * the second round trip becomes pure overhead. Computed SORTERS deliberately
 * keep two-phase — a computed sort pays the same WHERE-side cost either way,
 * but single-phase would additionally materialize every column of every row.
 */
function canUseTwoPhaseList(transforms: TransformRegistry, params: GetListParams): boolean {
  if (!transforms.twoPhaseList?.includes(params.resource)) return false;
  if ((params.pagination?.mode ?? "server") !== "server") return false;
  const pageSize = params.pagination?.pageSize ?? 10;
  if (pageSize < 2 || pageSize > 200) return false;
  if (sortHasDottedField(params.sorters)) return false;
  if (someFilterField(params.filters, (field) => field.includes("."))) return false;
  const computed = transforms.twoPhaseComputedFields?.[params.resource];
  if (computed?.length && someFilterField(params.filters, (field) => computed.includes(field))) return false;
  return true;
}

/**
 * Wrap a Refine DataProvider to apply per-resource transforms.
 *
 * - getList / getOne / getMany: applies registered read transform to response data
 * - create / update: applies registered write transform to values before sending
 * - Resources without a registered transform pass through unchanged
 */
export function createAppDataProvider(
  base: DataProvider,
  transforms: TransformRegistry,
): DataProvider {
  // Hoisted so the `getMany` closure below narrows on a const rather than an
  // optional property read.
  const baseGetMany = base.getMany;
  return {
    ...base,

    // ── Read transforms ──────────────────────────────────────────────────
    getList: async <TData extends BaseRecord = BaseRecord>(
      params: GetListParams,
    ): Promise<GetListResponse<TData>> => {
      const sanitized = params.filters
        ? { ...params, filters: sanitizeInFilters(params.filters) }
        : params;

      if (baseGetMany && canUseTwoPhaseList(transforms, params)) {
        // Phase 1: id-only page — same filters/sorters/range as a normal
        // request, but selecting only "id" means Postgres never materializes
        // the expensive view's other columns for the discarded rows.
        const idPage = await base.getList<BaseRecord>({
          ...sanitized,
          meta: { ...params.meta, select: "id" },
        });
        const ids = idPage.data.map((r) => r.id).filter((id): id is BaseKey => id != null);
        if (ids.length === 0) return { data: [], total: idPage.total };

        // Phase 2: full rows for exactly those ids. getMany applies no
        // ordering or count of its own, so phase-1 order and total win below.
        const manyResult = await baseGetMany<TData>({
          resource: params.resource,
          ids,
          meta: params.meta,
        });
        const byId = new Map(manyResult.data.map((r) => [r.id, r]));
        const ordered = ids.map((id) => byId.get(id)).filter((r): r is TData => r !== undefined);

        const transform = transforms.read[params.resource];
        if (!transform) return { data: ordered, total: idPage.total };
        // Null-guarded — see getOne for rationale.
        return {
          data: ordered.map((r) => (r == null ? r : (transform(r) as typeof r))),
          total: idPage.total,
        };
      }

      const result = await base.getList<TData>(sanitized);
      const transform = transforms.read[params.resource];
      if (!transform) return result;
      // Null-guarded — see getOne for rationale. getList typically returns
      // no nulls but the guard is cheap and survives provider changes.
      return { ...result, data: result.data.map((r) => (r == null ? r : (transform(r) as typeof r))) };
    },

    getOne: async <TData extends BaseRecord = BaseRecord>(
      params: GetOneParams,
    ): Promise<GetOneResponse<TData>> => {
      const result = await base.getOne<TData>(params);
      const transform = transforms.read[params.resource];
      if (!transform) return result;
      // Guard against missing rows — getOne can resolve to no row (RLS,
      // deleted, missing id) and the supabase data provider returns
      // `{ data: undefined }` in that case. Transforms expect a row in,
      // row out, so skip the transform on null/undefined.
      if (result.data == null) return result;
      return { ...result, data: transform(result.data) as typeof result.data };
    },

    getMany: baseGetMany
      ? async <TData extends BaseRecord = BaseRecord>(
          params: GetManyParams,
        ): Promise<GetManyResponse<TData>> => {
          const result = await baseGetMany<TData>(params);
          const transform = transforms.read[params.resource];
          if (!transform) return result;
          // Same null guard as getOne — supabase-js can return sparse
          // arrays when some ids resolve to no row.
          return { ...result, data: result.data.map((r) => (r == null ? r : (transform(r) as typeof r))) };
        }
      : undefined,

    // ── Write transforms ─────────────────────────────────────────────────
    create: async (params) => {
      const variables = writeVariables(params.variables, transforms.write[params.resource]);
      return base.create({ ...params, variables: variables as typeof params.variables });
    },

    update: async (params) => {
      const variables = writeVariables(params.variables, transforms.write[params.resource]);
      return base.update({ ...params, variables: variables as typeof params.variables });
    },
  };
}
